import { Form } from 'antd';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { Contract } from 'entities/Contract';
import { FC, memo, useEffect } from 'react';
import { Button } from 'shared/ui/Button';
import { InputNumber } from 'shared/ui/InputNumber';
import { Modal } from 'shared/ui/Modal';

interface RejectPriceRaiseForm {
  months: number;
}

interface RejectPriceRaiseModalProps {
  isOpened: boolean;
  contract: Nullable<Contract>;
  isLoading?: boolean;
  onCancel: () => void;
  onConfirm: (months: number) => void;
}

export const RejectPriceRaiseModal: FC<RejectPriceRaiseModalProps> = memo((props) => {
  const { isOpened, contract, isLoading, onCancel, onConfirm } = props;

  const { t } = useAppTranslation(['contracts', 'common']);

  const [form] = Form.useForm<RejectPriceRaiseForm>();

  useEffect(() => {
    if (isOpened) {
      form.setFieldsValue({ months: 3 });
    }
  }, [form, isOpened]);

  const handleFinish = (values: RejectPriceRaiseForm): void => {
    onConfirm(values.months);
  };

  return (
    <Modal isOpen={isOpened} onClose={onCancel} width={480} zIndex={1200}>
      <Form form={form} layout="vertical" onFinish={handleFinish} className="flex flex-col">
        <div className="font-semibold text-xl mb-3">{t('Reject price raise')}</div>
        <div className="text-primaryLight font-normal mb-6">
          {contract && (
            <div className="mb-3">
              {t('Are you sure you want to reject this price raise for contract {{contractNumber}}?', {
                contractNumber: contract.contractNumber,
              })}
            </div>
          )}
          <div className="mb-3">{t('Select when to show this contract again')}</div>
          <Form.Item
            label={t('Months')}
            name="months"
            rules={[{ required: true, message: `${t('Please, enter months')}!` }]}
            className="mb-0"
          >
            <InputNumber bordered min={1} placeholder={t('Months')} />
          </Form.Item>
        </div>
        <div className="flex justify-end gap-3">
          <Button theme="secondary" onClick={onCancel}>
            {t('Cancel', { ns: 'common' })}
          </Button>
          <Button type="submit" theme="danger" isLoading={isLoading} isDisabled={!contract}>
            {t('Reject')}
          </Button>
        </div>
      </Form>
    </Modal>
  );
});
